import React, { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { Server } from "@wajeez/wajeez.microservices";
import { Input } from "@ui/input";
import { updateUsername, useAppDispatch } from "@lib/stores";
import { AuthActions } from "@lib/stores/auth/slices";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";

const ChangeNameRow = ({ user }: { user: Server.AuthenticateOutput }) => {
  const dispatch = useAppDispatch();
  const [editState, setEditState] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<{ name: string }>({
    defaultValues: { name: user.name || "" },
    mode: "all",
  });

  useEffect(() => {
    setValue("name", user.name || "");
    reset({ name: user.name || "" });
  }, [user.name, setValue, reset]);

  const buttonText = useMemo(() => {
    if (user.name) {
      return editState ? "save" : "edit";
    }
    return editState ? "save" : "enter your name";
  }, [user.name, editState]);

  const onSubmit = async (data: { name: string }) => {
    try {
      if (isDirty) {
        await updateUsername({ name: data.name }, () => {
          dispatch(AuthActions.getMyInfoAsync());
        });
      }
      setEditState(false);
      reset();
    } catch (error) {
      console.error("Error updating username:", error);
    }
  };

  return (
    <div className="grid w-full grid-cols-3 items-center py-5">
      <TranslatedText
        text={`settings.titles.${"name"}`}
        className="text-black dark:text-white"
      />
      {editState ? (
        <Controller
          name="name"
          control={control}
          rules={{
            required: translate(`settings.errors.${"name is required"}`),
          }}
          render={({ field }) => (
            <div className="flex flex-col">
              <Input {...field} className="input-class" />
              {errors.name && (
                <p className="mt-1 self-start text-[12px] text-red-500">
                  {errors.name.message}
                </p>
              )}
            </div>
          )}
        />
      ) : user.name ? (
        <div className="ltr:text-left rtl:text-right dark:text-white">
          {user.name}
        </div>
      ) : (
        <TranslatedText
          text={`settings.titles.${"not found"}`}
          className="text-black dark:text-white"
        />
      )}
      <ShadcnButton
        className="bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent"
        action={editState ? handleSubmit(onSubmit) : () => setEditState(true)}
      >
        <TranslatedText
          text={`settings.titles.${buttonText}`}
          className="w-full cursor-pointer text-[15px] font-medium text-primary hover:opacity-70 ltr:text-right rtl:text-left dark:text-white"
        />
      </ShadcnButton>
    </div>
  );
};

export default ChangeNameRow;
